import { type InvestmentHolding, useInvestmentAccountDetail } from '@egw/api'
import { GenericError, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { InfiniteScrollWrapper } from '@egw/layouts/src/components'
import { Currency, getCurrencySymbol, getFormattedBalanceForOverview } from '@egw/utils'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { Link, useParams } from 'react-router'
import { extractAllocationHoldingsList } from '../../utils'

const PAGE_SIZE = 20

export const AllocationHoldingsList: FC = () => {
  const { alias = '', assetAllocation = '' } = useParams<{
    alias: string
    assetAllocation: string
  }>()

  const { t } = useTranslation('features-holdings')

  const {
    data: investmentAccountDetail,
    isLoading,
    isError,
  } = useInvestmentAccountDetail(parseInt(alias), {
    enabled: Boolean(alias),
  })

  const data = extractAllocationHoldingsList({
    data: investmentAccountDetail,
    assetAllocationParam: assetAllocation,
  })

  const pageSubtitle = investmentAccountDetail?.accountName

  const pageTitle = assetAllocation ? t(`allocation_holdings_list.subtitles.${assetAllocation}`) : ''

  const formattedTotalBalance = data?.amount ? getFormattedBalanceForOverview(data.amount) : undefined

  const [page, setPage] = useState<number>(1)

  const [loadingMoreTransactions, setLoadingMoreTransactions] = useState<boolean>(false)

  // Get total pages based on the number of transactions and the page size
  const getTotalPages = useMemo((): number => {
    if (!data?.holdings.length) return 0
    return Math.ceil(data.holdings.length / PAGE_SIZE)
  }, [data])

  // Load more transactions
  const onLoadMore = useCallback(() => {
    setLoadingMoreTransactions(true)

    const timeoutId = setTimeout(() => {
      setLoadingMoreTransactions(false)
      setPage((currPage) => currPage + 1)
    }, 500)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [])

  useEffect(() => {
    return () => {
      setLoadingMoreTransactions(false)
    }
  }, [])

  // Paginate transactions
  const paginatedHoldings = useMemo((): InvestmentHolding[] => {
    if (!data?.holdings) return []
    const endIndex = page * PAGE_SIZE
    return data.holdings.slice(0, endIndex)
  }, [data, page])

  // Check if there are more transactions to load
  const hasMoreTransactions = useMemo((): boolean => {
    return page < getTotalPages
  }, [page, getTotalPages])

  return (
    <>
      {isError ? (
        <div className="egw-container">
          <GenericError errorMessage={t('holdings.overview_fatal_error')} />
        </div>
      ) : null}
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {!isLoading && data ? (
        <InfiniteScrollWrapper
          onLoadMore={onLoadMore}
          hasMore={hasMoreTransactions}
          isLoading={loadingMoreTransactions}
        >
          <PageHeader
            title={pageTitle}
            subtitle={pageSubtitle}
            backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_LIST.backPath?.replace(
              ':alias',
              alias.toString(),
            )}
          />
          <div className="egw-container">
            <span
              data-testid="overview-customer-networth"
              className="mb-1.5 font-noto font-light text-3xl md:text-4xl lg:text-5xl"
              id="total_balance"
            >
              <span
                data-testid="overview-networth-currency"
                className="text-text-meta typo-primary-xl-light relative bottom-0 md:bottom-[.625rem]"
              >
                {getCurrencySymbol(String(data.amount.currencyCode))}
              </span>
              <span
                data-testid="overview-networth-whole"
                className="text-text-header left-12 text-right typo-primary-3xl-semibold"
              >
                {formattedTotalBalance?.integers}
              </span>
              <span data-testid="overview-networth-decimal" className="typo-primary-2xl-light text-text-meta">
                {formattedTotalBalance?.cents}
              </span>
            </span>

            <div className="mt-8 flex flex-col items-start justify-start ">
              {
                // map over categories
                paginatedHoldings.map(({ name, symbol, amount }: InvestmentHolding) => {
                  return (
                    <Link
                      to={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_DETAILS.path
                        .replace(':alias', alias.toString())
                        .replace(':assetAllocation', assetAllocation)
                        .replace(':symbol', symbol)}
                      key={symbol}
                      className="flex w-full flex-row justify-between border-light last-of-type:border-b-0 border-b py-6"
                    >
                      <div className="flex flex-row gap-7">
                        {/* name */}
                        <div className="flex flex-col items-start">
                          <p className="typo-primary-base-regular text-text-body">{name}</p>
                          <p className="typo-primary-sm-regular text-text-meta">{symbol}</p>
                        </div>
                      </div>
                      {/* money */}
                      <div className="flex flex-row justify-between ">
                        <div className="flex flex-row gap-1">
                          <div className="text-text-meta typo-primary-base-light font-semibold">
                            {amount.currencyCode ? getCurrencySymbol(amount.currencyCode) : null}
                          </div>
                          <span className="text-text-header typo-primary-base-semibold">
                            {Currency.integerCents.onlyMinus(amount.amount)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  )
                })
              }
              {loadingMoreTransactions ? (
                <div className="w-full">
                  <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
                    <Skeleton count={5} height="50px" />
                  </SkeletonTheme>
                </div>
              ) : null}

              {!hasMoreTransactions && paginatedHoldings.length > 0 && (
                <div className="w-full flex justify-center my-4 typo-primary-xs-regular text-text-meta">
                  {t('holdings.end_of_holdings')}
                </div>
              )}
            </div>
          </div>
        </InfiniteScrollWrapper>
      ) : null}
    </>
  )
}
